import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { UserNotesPanel } from "@/components/notes/UserNotesPanel";
import { entityDetailKey } from "@/lib/queryKeys";

interface CompanyDrawerContentProps {
  id: number;
}

/**
 * CompanyDrawerContent
 * Anteprima rapida di un'azienda nel RecordDrawer.
 * Dati da /api/companies/:id.
 * Consente modifica rapida dei campi principali e aggiunta note (notes_user).
 */
export default function CompanyDrawerContent({ id }: CompanyDrawerContentProps) {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", email: "", phone: "" });

  const { data: company, isLoading, isError } = useQuery({
    queryKey: entityDetailKey('companies', id),
    queryFn: () => apiRequest(`/api/companies/${id}`),
    enabled: id != null,
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiRequest("PATCH", `/api/companies/${id}`, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(entityDetailKey('companies', id), (current: any) => ({
        ...(current || {}),
        ...(updated || {}),
      }));
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/emails"] });
    },
    onError: () => {
      toast({ title: "Errore durante il salvataggio", variant: "destructive" });
    },
  });

  if (isLoading) {
    return <div className="p-5 text-muted-foreground">Caricamento...</div>;
  }

  if (isError || !company) {
    return <div className="p-5 text-destructive">Errore caricamento azienda</div>;
  }

  const website: string | undefined = company.website;
  const websiteHref = website
    ? website.startsWith("http")
      ? website
      : `https://${website}`
    : undefined;

  const startEditing = () => {
    setDraft({
      name: company.name || "",
      email: company.email || "",
      phone: company.phone || "",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    updateMutation.mutate(
      {
        name: draft.name.trim(),
        email: draft.email.trim() || null,
        phone: draft.phone.trim() || null,
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          toast({ title: "Azienda aggiornata" });
        },
      }
    );
  };


  return (
    <div className="flex flex-col">
      <div className="p-5 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          {company.name || "Azienda senza nome"}
        </h2>
        <div className="mt-2 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(`/companies/${id}`)}
            className="text-sm font-medium text-primary hover:underline"
          >
            Apri scheda completa
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={startEditing}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
            >
              Modifica
            </button>
          )}
        </div>
      </div>

      {company.industry && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Settore</div>
          <Badge variant="outline" className="text-xs">{company.industry}</Badge>
        </div>
      )}

      {isEditing ? (
        <div className="p-5 border-b border-border space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Nome</div>
            <Input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
            <Input
              value={draft.phone}
              onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 rounded-lg"
            >
              Salva
            </button>
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              disabled={updateMutation.isPending}
              className="border border-border bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted rounded-lg"
            >
              Annulla
            </button>
          </div>
        </div>
      ) : (
        <>
          {company.email && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Email</div>
              <a href={`mailto:${company.email}`} className="text-primary hover:underline">
                {company.email}
              </a>
            </div>
          )}

          {company.phone && (
            <div className="p-5 border-b border-border">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Telefono</div>
              <div className="text-foreground">{company.phone}</div>
            </div>
          )}
        </>
      )}

      {websiteHref && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Sito web</div>
          <a
            href={websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline break-all"
          >
            {website}
          </a>
        </div>
      )}

      {(company.country || company.address || company.fullAddress) && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Indirizzo</div>
          <div className="text-foreground">
            {company.fullAddress || company.address || company.country}
          </div>
        </div>
      )}

      {(company.employeeCount != null || (company as any).employees != null) && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Dipendenti</div>
          <div className="text-foreground">{company.employeeCount ?? (company as any).employees}</div>
        </div>
      )}

      {Array.isArray(company.tags) && company.tags.length > 0 && (
        <div className="p-5 border-b border-border">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Tag</div>
          <div className="flex flex-wrap gap-1">
            {company.tags.map((tag: string, i: number) => (
              <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
            ))}
          </div>
        </div>
      )}

      <div className="p-5 border-b border-border">
        <UserNotesPanel entityType="company" entityId={id} notesUser={company.notes_user} />
      </div>
    </div>
  );
}
